<template>
    <a
        class="l-ss-c-offer"
        ref="offerEl"
        :class="{
            'l-ss-c-offer-vertical': isVertical,
            'l-ss-c-offer-mobile': mobile,
            'l-ss-c-offer-adult': isAdult,
            'l-ss-c-offer-not-available': !offer.available,
            [`l-ss-c-offer-${camelToKebab(offerLayout)}`]: true,
        }"
        :data-first-offer-id="offer.id"
        :data-offer-id="currentOffer.id"
        :data-offer-index="offerIndex"
        :href="currentOffer.url"
        :target="linkTargetBlank ? '_blank' : '_self'"
        @click="onclick">
        <offer-item
            v-for="contentItem in offerContentItems"
            ref="offerItem"
            :key="contentItem.key"
            :content-item="contentItem"/>
    </a>
</template>

<script>
    import { ref, watch, onBeforeUnmount, onMounted } from '@vue/composition-api';
    import useOfferItemsResolver from '@/core/components/offer/use-offer-items-resolver';

    import useOfferGetPriceController from '@/core/components/offer/offer-price/use-offer-get-price-controller';

    import OfferItem from '@/core/components/offer/offer-item.vue';

    import camelToKebab from '@/core/helpers/camel-to-kebab';

    import { isVertical as checkIsVertical, isMini as checkIsMini } from '@/core/helpers/offer-layout';

    import { nanoid } from 'nanoid';

    export default {
        components: {
            OfferItem,
        },
        props: {
            offer: {
                type: Object,
            },
            settings: {
                type: Object,
                required: true,
            },
            layout: {
                type: String,
                default: 'row',
            },
            offerIndex: {
                type: Number,
                required: true,
            },
            mobile: {
                type: Boolean,
                default: false,
            },
            isAdult: {
                type: Boolean,
                default: false,
            },
            linkTargetBlank: {
                type: Boolean,
                default: false,
            },
            isCartCountAvailable: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, { emit, refs, root }) {
            const { offer, settings } = props;
            const { layout: layoutSettings, cart: cartSettings } = settings;

            const currentOffer = ref(offer);
            const offerItem = ref(null);

            const isShowDropdown = ref();

            let memo = {};

            let offerLayout = props.layout;
            let isVertical = checkIsVertical(offerLayout);
            let isMini = checkIsMini(offerLayout);

            let contentSettings = layoutSettings[offerLayout];

            let destroyComponentsFunc = null;

            const offerCartId = nanoid();

            const prepareOfferEvents = (eventsObj) => {
                const preparedEventsObj = {};

                Object.entries(eventsObj).forEach(([eventName, eventHandler]) => {
                    preparedEventsObj[eventName] = (event) => {
                        event.offer = currentOffer.value;
                        event.offerDataset = refs.offerEl.dataset;
                        eventHandler(event);
                    };
                });

                return preparedEventsObj;
            };

            const offerEvents = prepareOfferEvents({
                emitAddToCartClick: (event) => emit('add-to-cart-click', event),
                emitRemoveFromCartClick: (event) => emit('remove-from-cart-click', event),
                emitAddToFavouritesClick: (event) => emit('add-to-favourites-click', event),
                emitRemoveFromFavouritesClick: (event) => emit('remove-from-favourites-click', event),
                emitAddToCompareClick: (event) => emit('add-to-compare-click', event),
                emitRemoveFromCompareClick: (event) => emit('remove-from-compare-click', event),
                emitLoadGroupedOffers: (event) => emit('load-grouped-offers', event),
                emitCustomButtonClick: (event) => emit('custom-button-click', event),
                emitClick: (event) => emit('click', event),
            });

            const offerContentItems = ref([]);

            const initOfferContentItems = (rerender) =>
                useOfferItemsResolver(
                    {
                        items: contentSettings,
                        offer: currentOffer.value,
                        firstOfferId: offerCartId,
                        offerSettings: {
                            ...settings,
                            mobile: props.mobile,
                            vertical: isVertical,
                            isAdult: props.isAdult,
                            offerItem: offerItem,
                            isCartCountAvailable: props.isCartCountAvailable,
                            memo,
                            isShowDropdown,
                            customButton: settings.customButton,
                        },
                        on: {
                            addToCartClick: offerEvents.emitAddToCartClick,
                            removeFromCartClick: offerEvents.emitRemoveFromCartClick,
                            addToFavouritesClick: offerEvents.emitAddToFavouritesClick,
                            removeFromFavouritesClick: offerEvents.emitRemoveFromFavouritesClick,
                            addToCompareClick: offerEvents.emitAddToCompareClick,
                            removeFromCompareClick: offerEvents.emitRemoveFromCompareClick,
                            loadGroupedOffers: offerEvents.emitLoadGroupedOffers,
                            customButtonClick: offerEvents.emitCustomButtonClick,
                            changeMemo,
                            changeOffer: onChangeOfferByParam,
                            similarClickHandler: similarClickHandler,
                        },
                        root,
                        rerender,
                    });

            const init = (rerender) => {
                const { destroyComponents, contentItems } = initOfferContentItems(rerender);
                offerContentItems.value = contentItems;
                destroyComponentsFunc = destroyComponents;
            };

            onMounted(() => {
                init();
            });

            onBeforeUnmount(() => {
                destroyComponentsFunc();
            });

            const onclick = (event) => {
                if (event.target.closest('.l-ss-c-offer-cart-btn')) {
                    return;
                }

                offerEvents.emitClick(event);
            };

            const changeMemo = (data) => {
                const keyTokens = data.key.split('.');
                let current = memo;

                for (let i = 0; i < keyTokens.length - 1; ++i) {
                    const key = keyTokens[i];
                    if (!current[key]) {
                        current[key] = {};
                    }
                    current = current[key];
                }

                const lastKey = keyTokens[keyTokens.length - 1];
                current[lastKey] = data.value;
            };

            const updatePrice = async () => {
                const priceData = await useOfferGetPriceController(offer, settings.getPrice);
                currentOffer.value = {
                    ...currentOffer.value,
                    price: priceData.price,
                    oldPrice: priceData.oldPrice,
                };

                init(['price', 'labels']);
            };

            if (settings.getPrice) {
                updatePrice();
            }

            const similarClickHandler = (data) => {
                isShowDropdown.value = data;
            };

            const onChangeOfferByParam = (offer) => {
                currentOffer.value = offer;
                init(true);
            };

            watch(() => props.isAdult, () => {
                init();
            });

            return {
                currentOffer,
                offerItem,
                cartSettings,
                offerContentItems,
                isVertical,
                offerLayout,
                isMini,
                onclick,
                camelToKebab,
            };
        },
    };

</script>
